/**
 * Where to go after an SSO login. Same rule as the API
 * (apps/backend/src/auth/sso/return-to.ts): a relative path that starts with
 * one `/`, no `//`, no backslash, no control characters, no encoded
 * slash/backslash tricks, at most 512 characters and not a login page.
 * Anything else becomes `/`.
 */
export const SSO_RETURN_TO_MAX_LENGTH = 512;

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

const decodeFully = (value: string): string | null => {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (next === current) return current;
    current = next;
  }
  return null;
};

const isUnsafe = (path: string): boolean => !path.startsWith('/') || path.startsWith('//') || path.includes('\\') || CONTROL.test(path);

export const sanitizeSsoReturnTo = (raw: unknown): string => {
  if (typeof raw !== 'string' || raw === '') return '/';
  if (raw.length > SSO_RETURN_TO_MAX_LENGTH) return '/';
  if (isUnsafe(raw)) return '/';
  const decoded = decodeFully(raw);
  if (decoded === null || isUnsafe(decoded)) return '/';
  const pathOnly = decoded.split(/[?#]/)[0].toLowerCase();
  if (pathOnly.startsWith('/login')) return '/';
  try {
    const url = new URL(raw, 'https://anonify.invalid');
    if (url.origin !== 'https://anonify.invalid') return '/';
  } catch {
    return '/';
  }
  return raw;
};
